import { useEffect, useState, useMemo, useRef } from "react";
import { api, fileUrl } from "../../lib/api";
import { formatApiError } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Link2, CheckCircle2, AlertTriangle, Search, RefreshCw, History } from "lucide-react";

export default function AdminDspLinks() {
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Administrator · Songs</div>
      <h1 className="mt-2 text-4xl font-bold tracking-tight flex items-center gap-3">
        <Link2 className="w-8 h-8 text-cyan-400" /> Import DSP Links
      </h1>
      <p className="mt-2 text-zinc-500 text-sm">Upload one Excel that lists songs and their platform links. SonotekDigi matches each row to the correct existing song and updates only the DSP links — never overwriting audio, metadata, revenue or ownership.</p>

      <Tabs defaultValue="import" className="mt-8">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="import" data-testid="tab-import">Import</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>
        <TabsContent value="import"><ImportFlow /></TabsContent>
        <TabsContent value="history"><HistoryTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function ImportFlow() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [manualFor, setManualFor] = useState(null);
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [rowOverwrite, setRowOverwrite] = useState({}); // { [rowIndex]: [platform,...] }
  const fileRef = useRef(null);

  const runPreview = async () => {
    if (!file) { toast.error("Choose an Excel file first"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/dsp-links/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success(`Parsed ${data.counters.total} rows — ${data.counters.songs_to_update} songs will be updated`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Preview failed"); }
    finally { setLoading(false); }
  };

  const confirm = async () => {
    if (!preview) return;
    setConfirming(true);
    try {
      const overwrite = {};
      Object.entries(rowOverwrite).forEach(([rowIdx, platforms]) => {
        if (platforms && platforms.length) overwrite[rowIdx] = platforms;
      });
      const { data } = await api.post(`/admin/dsp-links/confirm/${preview.preview_id}`, { overwrite });
      toast.success(`Import applied — ${data.updated_songs} songs updated, ${data.updated_platforms} platform links added, ${data.kept_conflicts || 0} existing links kept`);
      setPreview(null); setFile(null); setRowOverwrite({});
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Confirm failed"); }
    finally { setConfirming(false); }
  };

  const openManual = async (row) => {
    setManualFor(row); setQuery(""); setCandidates([]);
    const { data } = await api.get("/admin/submissions").catch(() => ({ data: [] }));
    setCandidates(data);
  };
  const filteredCandidates = useMemo(() => {
    if (!query) return candidates.slice(0, 15);
    const q = query.toLowerCase();
    return candidates.filter((s) =>
      (s.song_title || "").toLowerCase().includes(q) ||
      (s.isrc || "").toLowerCase().includes(q) ||
      (s.upc || "").toLowerCase().includes(q) ||
      (s.primary_artist_name || "").toLowerCase().includes(q) ||
      (s.user?.full_name || "").toLowerCase().includes(q)
    ).slice(0, 15);
  }, [candidates, query]);

  const manualLink = async (sub) => {
    try {
      await api.post("/admin/dsp-links/manual-link", {
        preview_id: preview.preview_id,
        row_index: manualFor.row_index,
        submission_id: sub.id,
      });
      // reflect locally
      const rows = preview.rows.map((r) => r.row_index === manualFor.row_index ? {
        ...r, status: "matched", match_type: "manual",
        candidate_submission_id: sub.id, matched_submission_ids: [sub.id],
      } : r);
      const counters = { ...preview.counters };
      counters.unmatched = Math.max(0, counters.unmatched - 1);
      counters.songs_to_update = rows.filter((r) => r.status === "matched" && Object.keys(r.platform_links || {}).length).length;
      setPreview({ ...preview, rows, counters });
      setManualFor(null);
      toast.success(`Linked to ${sub.song_title}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Link failed"); }
  };

  const c = preview?.counters || {};
  const unmatchedRows = preview?.rows?.filter((r) => r.status === "unmatched" || r.status === "ambiguous") || [];
  const matchedRows = preview?.rows?.filter((r) => r.status === "matched") || [];

  return (
    <div className="mt-6 space-y-6">
      {/* Upload */}
      <div className="p-6 rounded-2xl border border-zinc-800 bg-zinc-950/60">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[280px]">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Links Excel (.xlsx)</Label>
            <Input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={(e) => { setFile(e.target.files?.[0]); setPreview(null); }}
              data-testid="dsp-file" className="mt-1.5 bg-zinc-900 border-zinc-800 file:mr-3 file:rounded-md file:border-0 file:bg-zinc-800 file:text-zinc-200 file:px-2 file:py-1" />
          </div>
          <Button onClick={runPreview} disabled={!file || loading} data-testid="dsp-preview-btn"
            className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> {loading ? "Analyzing..." : "Preview Import"}
          </Button>
        </div>
        <p className="mt-3 text-xs text-zinc-500">Nothing is written to the database until you click <strong>Confirm Import</strong>.</p>
      </div>

      {preview && (
        <>
          {/* Diagnostics */}
          {preview.diagnostics && (
            <div className="p-4 rounded-2xl border border-zinc-800 bg-zinc-950/60 text-xs">
              <div className="font-semibold text-zinc-300 mb-2">Import Diagnostics</div>
              <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-zinc-400">
                <div>Detected headers → <span className="text-cyan-400 font-mono">SONG={preview.diagnostics.detected_headers.song || "—"}</span>, <span className="text-cyan-400 font-mono">ISRC={preview.diagnostics.detected_headers.isrc || "—"}</span>, <span className="text-cyan-400 font-mono">UPC={preview.diagnostics.detected_headers.upc || "—"}</span>, <span className="text-cyan-400 font-mono">ARTIST={preview.diagnostics.detected_headers.artist || "—"}</span></div>
                <div>Platform columns found: <span className="text-emerald-400 font-mono">{preview.diagnostics.detected_platforms.join(", ") || "none"}</span></div>
                <div>DB songs indexed: <span className="text-zinc-100 font-mono">{preview.diagnostics.db_songs_indexed}</span> — with ISRC: <span className="text-emerald-400 font-mono">{preview.diagnostics.db_songs_with_isrc}</span> — with UPC: <span className="text-emerald-400 font-mono">{preview.diagnostics.db_songs_with_upc}</span></div>
                {preview.diagnostics.unknown_platform_headers?.length > 0 && (
                  <div className="text-amber-400">Unknown link_ headers ignored: {preview.diagnostics.unknown_platform_headers.join(", ")}</div>
                )}
              </div>
            </div>
          )}

          {/* Counters */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            <Stat label="Total Rows" value={c.total} />
            <Stat label="ISRC" value={c.isrc_matches} tone="emerald" />
            <Stat label="UPC" value={c.upc_matches} tone="cyan" />
            <Stat label="Song+Singer" value={c.song_singer_matches} tone="violet" />
            <Stat label="Song Only" value={c.song_only_matches} tone="violet" />
            <Stat label="With Links" value={c.rows_with_links} />
            <Stat label="Unmatched" value={c.unmatched} tone="rose" />
            <Stat label="Songs To Update" value={c.songs_to_update} tone="emerald" highlight />
          </div>

          <div className="flex justify-end">
            <Button onClick={confirm} disabled={confirming || (c.songs_to_update || 0) === 0}
              data-testid="dsp-confirm-btn"
              className="rounded-full bg-gradient-to-r from-emerald-400 to-cyan-400 text-zinc-950 font-semibold hover:brightness-110">
              <CheckCircle2 className="w-4 h-4 mr-2" /> {confirming ? "Applying..." : `Confirm Import (${c.songs_to_update || 0})`}
            </Button>
          </div>

          {/* Matched rows */}
          {matchedRows.length > 0 && (
            <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-x-auto">
              <div className="p-4 border-b border-zinc-800"><h3 className="font-semibold flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Matched rows ({matchedRows.length})</h3></div>
              <table className="w-full min-w-[900px] text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                  <th className="p-3">#</th><th className="p-3">Song</th><th className="p-3">Artist</th>
                  <th className="p-3">Match</th><th className="p-3">New Links</th><th className="p-3">Existing Links (kept by default)</th>
                </tr></thead>
                <tbody>
                  {matchedRows.slice(0, 500).map((r) => {
                    const overwriteSet = new Set(rowOverwrite[r.row_index] || []);
                    const toggleOverwrite = (platform) => {
                      const cur = new Set(rowOverwrite[r.row_index] || []);
                      if (cur.has(platform)) cur.delete(platform); else cur.add(platform);
                      setRowOverwrite({ ...rowOverwrite, [r.row_index]: Array.from(cur) });
                    };
                    return (
                      <tr key={r.row_index} data-testid={`preview-row-${r.row_index}`} className="border-b border-zinc-900 align-top">
                        <td className="p-3 font-mono text-zinc-500 text-xs">{r.row_index}</td>
                        <td className="p-3">
                          {r.song || "—"}
                          {r.matched_song_info?.song_title && r.matched_song_info?.song_title !== r.song && (
                            <div className="text-[10px] text-zinc-500">→ {r.matched_song_info.song_title}</div>
                          )}
                          <div className="text-[10px] font-mono text-zinc-600">
                            ISRC {r.isrc || "—"} · UPC {r.upc || "—"}
                          </div>
                        </td>
                        <td className="p-3">{r.artist || "—"}</td>
                        <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-xs">{r.match_type}</span></td>
                        <td className="p-3 text-xs">
                          {(r.will_add_platforms || []).length > 0 && (
                            <div className="text-emerald-400" data-testid={`will-add-${r.row_index}`}>
                              +{r.will_add_platforms.length}: {r.will_add_platforms.join(", ")}
                            </div>
                          )}
                          {(r.will_conflict_platforms || []).length > 0 && (
                            <div className="mt-1 text-amber-400" data-testid={`will-conflict-${r.row_index}`}>
                              {r.will_conflict_platforms.length} conflict(s) — kept unless ticked:
                              <div className="mt-1 space-y-0.5">
                                {r.conflicts.map((c) => (
                                  <label key={c.platform} className="flex items-start gap-1.5 cursor-pointer">
                                    <input type="checkbox" checked={overwriteSet.has(c.platform)} onChange={() => toggleOverwrite(c.platform)}
                                      data-testid={`overwrite-${r.row_index}-${c.platform}`} className="mt-0.5" />
                                    <span className="flex-1">
                                      <span className="text-zinc-400">{c.platform}:</span>{" "}
                                      <span className="text-zinc-500 line-through">{c.old.slice(0, 40)}</span>{" → "}
                                      <span className="text-emerald-400">{c.new.slice(0, 40)}</span>
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                          {(!r.will_add_platforms?.length && !r.will_conflict_platforms?.length) && (
                            <span className="text-zinc-500">no new links</span>
                          )}
                        </td>
                        <td className="p-3 text-xs text-zinc-500">
                          {(r.existing_platforms || []).length ? r.existing_platforms.join(", ") : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Unmatched */}
          {unmatchedRows.length > 0 && (
            <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 overflow-x-auto">
              <div className="p-4 border-b border-rose-500/30 flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-rose-400" /> Unmatched / Ambiguous ({unmatchedRows.length})</h3>
                <span className="text-xs text-zinc-500">Manually link to include in this import</span>
              </div>
              <table className="w-full min-w-[900px] text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-rose-500/30">
                  <th className="p-3">#</th><th className="p-3">Song</th><th className="p-3">Artist</th>
                  <th className="p-3">UPC</th><th className="p-3">ISRC</th>
                  <th className="p-3">Reason</th><th className="p-3 text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {unmatchedRows.map((r) => (
                    <tr key={r.row_index} data-testid={`unmatched-row-${r.row_index}`} className="border-b border-rose-500/10 align-top">
                      <td className="p-3 font-mono text-zinc-500 text-xs">{r.row_index}</td>
                      <td className="p-3">{r.song || "—"}</td>
                      <td className="p-3">{r.artist || "—"}</td>
                      <td className="p-3 font-mono text-xs">{r.upc || "—"}</td>
                      <td className="p-3 font-mono text-xs">{r.isrc || "—"}</td>
                      <td className="p-3 text-xs text-rose-300" data-testid={`reason-${r.row_index}`}>
                        {r.reason || r.status}
                      </td>
                      <td className="p-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => openManual(r)}
                          data-testid={`manual-link-${r.row_index}`} className="border-zinc-700 hover:bg-zinc-900 rounded-full text-cyan-400">
                          <Link2 className="w-3.5 h-3.5 mr-1" /> Link
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <Dialog open={!!manualFor} onOpenChange={(o) => !o && setManualFor(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="manual-link-dialog">
          <DialogHeader><DialogTitle>Manually link row to an existing SonotekDigi song</DialogTitle></DialogHeader>
          {manualFor && (
            <div className="space-y-3">
              <div className="p-3 rounded bg-zinc-900 border border-zinc-800 text-sm">
                <div><span className="text-zinc-500">Row #{manualFor.row_index}</span></div>
                <div className="mt-1"><strong>{manualFor.song || "—"}</strong> by {manualFor.artist || "—"}</div>
                <div className="text-xs text-zinc-500 mt-1 font-mono">UPC {manualFor.upc || "—"} · ISRC {manualFor.isrc || "—"}</div>
              </div>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by song, ISRC, UPC, artist or user..."
                  data-testid="manual-search" className="pl-9 bg-zinc-900 border-zinc-800" />
              </div>
              <div className="border border-zinc-800 rounded max-h-72 overflow-y-auto divide-y divide-zinc-900">
                {filteredCandidates.map((s) => (
                  <button key={s.id} onClick={() => manualLink(s)} data-testid={`candidate-${s.id}`}
                    className="w-full text-left p-3 hover:bg-zinc-900 flex items-center gap-3">
                    <img src={fileUrl(s.artwork_url)} alt="" className="w-10 h-10 rounded object-cover border border-zinc-800" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{s.song_title}</div>
                      <div className="text-xs text-zinc-500 truncate">{s.primary_artist_name || s.artist_name} · {s.user?.full_name}</div>
                      <div className="text-[10px] font-mono text-zinc-600">ISRC {s.isrc || "—"} · UPC {s.upc || "—"}</div>
                    </div>
                  </button>
                ))}
                {filteredCandidates.length === 0 && <div className="p-6 text-center text-zinc-500 text-sm">No matches</div>}
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setManualFor(null)} className="border-zinc-700">Cancel</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HistoryTab() {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const load = () => api.get("/admin/dsp-links/history").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const open = async (id) => {
    const { data } = await api.get(`/admin/dsp-links/history/${id}`);
    setDetail(data);
  };

  return (
    <div className="mt-6 space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" onClick={load} className="border-zinc-700 hover:bg-zinc-900 rounded-full">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
            <th className="p-4">Date</th><th className="p-4">File</th>
            <th className="p-4 text-right">Rows</th><th className="p-4 text-right">Updated Songs</th>
            <th className="p-4 text-right">Updated Links</th><th className="p-4 text-right">Unmatched</th>
            <th className="p-4 text-right">Failed</th><th className="p-4"></th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`history-${r.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                <td className="p-4 text-sm text-zinc-400">{new Date(r.imported_at).toLocaleString()}</td>
                <td className="p-4 text-sm"><FileSpreadsheet className="w-4 h-4 text-emerald-400 inline mr-2" />{r.filename}</td>
                <td className="p-4 text-right font-mono">{r.total_rows}</td>
                <td className="p-4 text-right font-mono text-emerald-400">{r.updated_songs}</td>
                <td className="p-4 text-right font-mono text-cyan-400">{r.updated_platforms}</td>
                <td className="p-4 text-right font-mono text-rose-400">{r.counters?.unmatched ?? 0}</td>
                <td className="p-4 text-right font-mono">{r.counters?.ambiguous ?? 0}</td>
                <td className="p-4 text-right"><Button size="sm" variant="ghost" onClick={() => open(r.id)} data-testid={`history-view-${r.id}`} className="text-cyan-400">View</Button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="8" className="p-12 text-center text-zinc-500">No imports yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="history-detail-dialog">
          <DialogHeader><DialogTitle>{detail?.filename}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-4 gap-2">
                <Stat label="Total" value={detail.total_rows} />
                <Stat label="Updated" value={detail.updated_songs} tone="emerald" />
                <Stat label="Unmatched" value={detail.unmatched?.length || 0} tone="rose" />
                <Stat label="Failed" value={detail.failed?.length || 0} tone="amber" />
              </div>
              {(detail.unmatched?.length || 0) > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Unmatched Rows</div>
                  <div className="border border-zinc-800 rounded max-h-64 overflow-y-auto divide-y divide-zinc-900">
                    {detail.unmatched.map((r, i) => (
                      <div key={i} className="p-3 text-xs">
                        <div><strong>{r.song || "—"}</strong> · {r.artist || "—"}</div>
                        <div className="text-zinc-500 font-mono">ISRC {r.isrc || "—"} · UPC {r.upc || "—"} · {r.status}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, tone, highlight }) {
  const t = { emerald: "border-emerald-500/30 from-emerald-500/10 text-emerald-400",
              cyan: "border-cyan-500/30 from-cyan-500/10 text-cyan-400",
              violet: "border-violet-500/30 from-violet-500/10 text-violet-400",
              amber: "border-amber-500/30 from-amber-500/10 text-amber-400",
              rose: "border-rose-500/30 from-rose-500/10 text-rose-400",
            }[tone] || "border-zinc-800 from-transparent text-zinc-200";
  const [ring, grad, txt] = t.split(" ");
  return (
    <div className={`p-3 rounded-xl border ${ring} bg-gradient-to-br ${grad} to-transparent ${highlight ? "ring-2 ring-emerald-500/40" : ""}`}>
      <div className={`text-2xl font-bold ${txt}`}>{value ?? "—"}</div>
      <div className="mt-0.5 text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
    </div>
  );
}
